/**
 * Verona Tempo r9 (U9) — "Quick add" on phone product cards. Vanilla JS, no dependencies.
 * Loaded (deferred) on every page from snippets/vt-quick-add-assets.liquid, after assets/vt-theme.js (uses window.vtCore).
 *  - The round "+" on a card photo (snippets/product-block.liquid; phones only, products in stock) opens a bottom sheet:
 *    photo, name, price, colour + size choices (sold-out sizes crossed out and disabled, "2 left" like the product page),
 *    "Add to bag" → /cart/add.js → the bag drawer opens (vtCore.openCartAfterAdd, the same path as outfit cards).
 *  - Products with one variant: the "+" adds straight away (a tick + a short "Added to your bag · View bag" note).
 *  - Sheet: role="dialog" + aria-modal, focus kept inside, Escape / tap outside / swipe down close it, focus goes back
 *    to the "+"; friendly error messages (vtCore.friendlyError); her remembered size is pre-chosen (size memory).
 * Product data: /products/<handle>.js (vtCore.fetchProduct); low-stock numbers: sections/vt-quick-add-data.liquid.
 * Texts: window.vtConfig.quickAdd.strings. Styles: assets/vt-quick-add.css. Setting: Verona Tempo > Quick add.
 */
(function () {
  'use strict';

  var core = window.vtCore;
  var QA = window.vtConfig && window.vtConfig.quickAdd;
  if (!core || !QA) return;
  var S = QA.strings || {};
  var CS = core.S || {};
  var tpl = core.tpl;
  var esc = core.esc;
  var route = core.route;
  document.documentElement.classList.add('vt-qa-js');

  var COLOUR_NAMES = ((core.cfg && core.cfg.colourNames) || ['Color', 'Colour']).concat(['اللون', 'لون'])
    .map(function (n) { return String(n).trim().toLowerCase(); });
  function isColour(name) { return COLOUR_NAMES.indexOf(String(name || '').trim().toLowerCase()) > -1; }
  function isSize(name) { return core.isSizeOption(name); }
  function swatchKey(v) { return String(v || '').replace(/"/g, '').toLowerCase(); }
  var stockCache = {};
  function fetchStock(handle) {
    if (!QA.lowStock) return Promise.resolve({});
    if (!stockCache[handle]) {
      stockCache[handle] = fetch(route('products/' + encodeURIComponent(handle) + '?section_id=vt-quick-add-data'))
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var m = html.match(/<script type="application\/json" data-vt-qa-data>([\s\S]*?)<\/script>/);
          return m ? (JSON.parse(m[1]).stock || {}) : {};
        })
        .catch(function () { return {}; });
    }
    return stockCache[handle];
  }
  function sectionIds() {
    return ['page-header', 'cart-drawer'].map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el && el.dataset.sectionId; })
      .map(function (el) { return el.dataset.sectionId; });
  }
  function addToCart(id) {
    return fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: sectionIds().join(','), sections_url: window.location.pathname })
    }).then(function (r) {
      return r.json().then(function (d) { return { ok: r.ok, data: d }; }, function () { return { ok: r.ok, data: {} }; });
    }).then(function (res) {
      if (!res.ok || res.data.status) {
        throw new Error(core.friendlyError((res.data && (res.data.description || res.data.message)) || ''));
      }
      return res.data;
    }, function () { throw new Error(core.friendlyError('network')); });
  }
  function updateSections(sections) {
    if (!sections) { document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true })); return; }
    ['page-header', 'cart-drawer'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el && el.dataset.sectionId && sections[el.dataset.sectionId] && el.updateFromCartChange) {
        try { el.updateFromCartChange(sections[el.dataset.sectionId]); } catch (e) { /* keep going */ }
      }
    });
  }
  function animOn() { return core.animationsOn ? core.animationsOn() : !core.reducedMotion(); }

  /* ------------------------------------------------------------ the sheet */
  var root = null;
  var els = {};
  var state = null;
  var closeTimer = null;

  var ICON_CLOSE = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function build() {
    if (root) return;
    root = document.createElement('div');
    root.className = 'vt-qa';
    root.hidden = true;
    root.innerHTML =
      '<div class="vt-qa__overlay" data-vt-qa-close></div>' +
      '<div class="vt-qa__sheet" role="dialog" aria-modal="true" aria-labelledby="vt-qa-title" tabindex="-1">' +
        '<div class="vt-qa__grab" aria-hidden="true"><span></span></div>' +
        '<button type="button" class="vt-qa__close" data-vt-qa-close aria-label="' + esc(S.close) + '">' + ICON_CLOSE + '</button>' +
        '<div class="vt-qa__head">' +
          '<div class="vt-qa__img"><img alt="" width="96" height="96" hidden></div>' +
          '<div class="vt-qa__info">' +
            '<h2 class="vt-qa__title" id="vt-qa-title"></h2>' +
            '<p class="vt-qa__price"></p>' +
            '<a class="vt-qa__link" href="#">' + esc(S.details) + '</a>' +
          '</div>' +
        '</div>' +
        '<div class="vt-qa__body"></div>' +
        '<p class="vt-qa__status" role="status" aria-live="polite"></p>' +
        '<div class="vt-qa__foot"><button type="button" class="btn vt-qa__add">' + esc(S.add) + '</button></div>' +
      '</div>';
    document.body.appendChild(root);
    els.sheet = root.querySelector('.vt-qa__sheet');
    els.img = root.querySelector('.vt-qa__img img');
    els.title = root.querySelector('.vt-qa__title');
    els.price = root.querySelector('.vt-qa__price');
    els.link = root.querySelector('.vt-qa__link');
    els.body = root.querySelector('.vt-qa__body');
    els.status = root.querySelector('.vt-qa__status');
    els.add = root.querySelector('.vt-qa__add');
    els.grab = root.querySelector('.vt-qa__grab');

    root.addEventListener('click', function (evt) {
      if (evt.target.closest('[data-vt-qa-close]')) { evt.preventDefault(); close(true); }
    });
    root.addEventListener('change', function (evt) {
      var input = evt.target.closest('.vt-qa__radio');
      if (!input || !state) return;
      state.selected[parseInt(input.dataset.index, 10)] = input.value;
      if (input.dataset.kind === 'colour') keepValidChoices(parseInt(input.dataset.index, 10));
      setStatus('');
      render(input.dataset.index);
    });
    els.add.addEventListener('click', submit);
    root.addEventListener('keydown', onKeydown);
    initSwipe();
  }

  function onKeydown(evt) {
    if (evt.key === 'Escape') { evt.preventDefault(); close(true); return; }
    if (evt.key !== 'Tab') return;
    var list = Array.prototype.slice.call(els.sheet.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])'))
      .filter(function (el) {
        if (el.type === 'radio' && !el.checked) {
          // only one stop per radio group: the checked one, or the first enabled one when none is checked
          var group = els.sheet.querySelectorAll('input[name="' + el.name + '"]');
          var checked = Array.prototype.some.call(group, function (g) { return g.checked; });
          var firstEnabled = Array.prototype.filter.call(group, function (g) { return !g.disabled; })[0];
          return !checked && el === firstEnabled;
        }
        return el.getClientRects().length > 0 || el.type === 'radio';
      });
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var active = document.activeElement;
    if (evt.shiftKey && (active === first || active === els.sheet)) { evt.preventDefault(); last.focus(); }
    else if (!evt.shiftKey && active === last) { evt.preventDefault(); first.focus(); }
  }

  function initSwipe() {
    var startY = null;
    var dy = 0;
    var onStart = function (evt) {
      if (els.sheet.scrollTop > 0 && !evt.target.closest('.vt-qa__grab')) return;
      startY = evt.touches[0].clientY;
      dy = 0;
      els.sheet.classList.add('is-dragging');
    };
    var onMove = function (evt) {
      if (startY === null) return;
      dy = Math.max(0, evt.touches[0].clientY - startY);
      els.sheet.style.transform = dy ? 'translateY(' + dy + 'px)' : '';
    };
    var onEnd = function () {
      if (startY === null) return;
      startY = null;
      els.sheet.classList.remove('is-dragging');
      // swiped far enough: finish the slide down from where her finger left it (inline transform wins over the CSS)
      if (dy > 90) { els.sheet.style.transform = animOn() ? 'translateY(100%)' : ''; close(true); } else { els.sheet.style.transform = ''; }
      dy = 0;
    };
    els.sheet.addEventListener('touchstart', onStart, { passive: true });
    els.sheet.addEventListener('touchmove', onMove, { passive: true });
    els.sheet.addEventListener('touchend', onEnd);
    els.sheet.addEventListener('touchcancel', onEnd);
  }

  function setStatus(msg, isError) {
    els.status.textContent = msg || '';
    els.status.classList.toggle('is-error', !!isError);
  }

  /* variants that match the chosen values (ignoring the options not chosen yet, and option "skip") */
  function matching(selected, skip) {
    return state.product.variants.filter(function (v) {
      for (var i = 0; i < selected.length; i += 1) {
        if (i === skip || selected[i] == null) continue;
        if (v.options[i] !== selected[i]) return false;
      }
      return true;
    });
  }
  function valueAvailable(index, value) {
    var sel = state.selected.slice();
    // colour: available when any size of it is in stock; other options: with the colour chosen
    if (state.kinds[index] === 'colour') sel = sel.map(function (s, i) { return state.kinds[i] === 'colour' ? s : null; });
    sel[index] = value;
    return matching(sel).some(function (v) { return v.available; });
  }
  function keepValidChoices(changed) {
    state.selected.forEach(function (val, i) {
      if (i === changed || val == null) return;
      if (!valueAvailable(i, val)) state.selected[i] = null;
    });
    autoPick();
  }
  function autoPick() {
    state.product.options.forEach(function (opt, i) {
      if (state.selected[i] != null) return;
      var avail = opt.values.filter(function (val) { return valueAvailable(i, val); });
      if (state.kinds[i] === 'size') {
        if (state.memory && avail.indexOf(state.memory) > -1) state.selected[i] = state.memory;
        else if (opt.values.length === 1 && avail.length === 1) state.selected[i] = avail[0];
      } else if (avail.length) {
        state.selected[i] = avail[0];
      }
    });
  }
  function currentVariant() {
    if (state.selected.some(function (s) { return s == null; })) return null;
    return matching(state.selected)[0] || null;
  }

  function priceHtml(variant) {
    var list = variant ? [variant] : matching(state.selected).filter(function (v) { return v.available; });
    if (!list.length) list = state.product.variants;
    var min = list.reduce(function (m, v) { return Math.min(m, v.price); }, Infinity);
    var cheapest = list.filter(function (v) { return v.price === min; })[0];
    var out = '<span class="vt-qa__now theme-money">' + esc(core.formatMoney(min)) + '</span>';
    if (cheapest && cheapest.compare_at_price && cheapest.compare_at_price > min) {
      out += ' <s class="vt-qa__was theme-money">' + esc(core.formatMoney(cheapest.compare_at_price)) + '</s>';
    }
    return out;
  }
  function photoFor() {
    var p = state.product;
    var ci = state.kinds.indexOf('colour');
    var src = null;
    if (ci > -1 && state.selected[ci] != null) {
      var withImg = p.variants.filter(function (v) { return v.options[ci] === state.selected[ci] && v.featured_image; })[0];
      if (withImg) src = withImg.featured_image.src;
    }
    return src || p.featured_image || (p.images && p.images[0]) || null;
  }

  function render(focusIndex) {
    var p = state.product;
    var html = '';
    p.options.forEach(function (opt, i) {
      if (p.variants.length === 1 && opt.values.length === 1 && /default title/i.test(opt.values[0])) return;
      var kind = state.kinds[i];
      var chosen = state.selected[i];
      var legend = chosen != null ? tpl(S.selected, { option: opt.name, value: chosen }) : opt.name;
      html += '<fieldset class="vt-qa__opt vt-qa__opt--' + kind + '"><legend class="vt-qa__legend">' + esc(legend) + '</legend><div class="vt-qa__values">';
      opt.values.forEach(function (val, vi) {
        var avail = valueAvailable(i, val);
        var id = 'vt-qa-' + i + '-' + vi;
        var low = '';
        if (kind === 'size' && avail && state.stock) {
          var sel = state.selected.slice();
          sel[i] = val;
          var v = sel.every(function (s) { return s != null; }) ? matching(sel)[0] : null;
          var qty = v && state.stock[v.id];
          if (qty) {
            low = '<span class="vt-qa__low" aria-hidden="true">' + esc(tpl(CS.lowStockBadge || '[count]', { count: qty })) + '</span>' +
              '<span class="visually-hidden">, ' + esc(tpl(CS.lowStockFull || '[count]', { count: qty })) + '</span>';
          }
        }
        html += '<label class="vt-qa__val' + (avail ? '' : ' is-sold') + (low ? ' is-low' : '') + '" for="' + id + '">' +
          '<input class="vt-qa__radio visually-hidden" type="radio" id="' + id + '" name="vt-qa-o' + i + '" value="' + esc(val) + '" data-index="' + i + '" data-kind="' + kind + '"' +
          (chosen === val ? ' checked' : '') + (!avail && kind !== 'colour' ? ' disabled' : '') + '>' +
          (kind === 'colour' ? '<span class="vt-qa__swatch" data-swatch="' + esc(swatchKey(val)) + '" aria-hidden="true"></span>' : '') +
          '<span class="vt-qa__name">' + esc(val) + '</span>' + low +
          (avail ? '' : '<span class="visually-hidden">, ' + esc(S.soldOut) + '</span>') +
          '</label>';
      });
      html += '</div></fieldset>';
    });
    els.body.innerHTML = html;
    var variant = currentVariant();
    els.price.innerHTML = priceHtml(variant);
    var src = photoFor();
    if (src) {
      els.img.src = core.imageUrl(src, 240);
      els.img.hidden = false;
    } else {
      els.img.hidden = true;
    }
    var canAdd = !variant || variant.available;
    els.add.disabled = !p.available;
    els.add.textContent = canAdd ? S.add : S.soldOut;
    if (focusIndex != null) {
      var again = els.body.querySelector('input[name="vt-qa-o' + focusIndex + '"]:checked');
      if (again) again.focus({ preventScroll: true });
    }
  }

  function kindOf(name) {
    if (isColour(name)) return 'colour';
    if (isSize(name)) return 'size';
    return 'text';
  }
  function sizeCategory(p) {
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('shoes') > -1) return 'shoes';
    if (tags.indexOf('bags') > -1) return 'bags';
    return String(p.type || '').toLowerCase().replace(/[^a-z0-9؀-ۿ]+/g, '-').replace(/^-|-$/g, '');
  }

  function open(btn) {
    build();
    window.clearTimeout(closeTimer);
    var block = btn.closest('product-block, .product-block');
    var titleEl = block && block.querySelector('.product-block__title');
    var link = block && block.querySelector('a.product-link[href]');
    var shownColour = block && block.querySelector('.product-block-options--swatch .vt-swatch--selected[data-option-item]');
    state = { handle: btn.dataset.vtQa, opener: btn, product: null, stock: {}, selected: [], kinds: [], memory: null,
      shownColour: shownColour ? shownColour.dataset.optionItem : null };
    els.title.textContent = titleEl ? titleEl.textContent.trim() : '';
    els.price.innerHTML = '';
    els.body.innerHTML = '<p class="vt-qa__loading">' + esc(S.loading) + '</p>';
    els.link.href = link ? link.getAttribute('href') : route('products/' + state.handle);
    els.img.hidden = true;
    els.add.disabled = true;
    els.add.textContent = S.add;
    els.add.removeAttribute('aria-busy');
    setStatus('');
    els.sheet.style.transform = '';
    root.hidden = false;
    document.documentElement.classList.add('vt-qa-lock');
    // next frame: slide up (only with animations on; otherwise it is simply there)
    window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { root.classList.add('is-open'); }); });
    els.sheet.focus({ preventScroll: true });

    var mine = state;
    Promise.all([core.fetchProduct(state.handle), fetchStock(state.handle)]).then(function (res) {
      if (state !== mine) return;
      var p = res[0];
      if (!p || !p.variants) { els.body.innerHTML = ''; setStatus(S.errorLoad, true); return; }
      state.product = p;
      state.stock = res[1] || {};
      state.kinds = p.options.map(function (o) { return kindOf(o.name); });
      state.selected = p.options.map(function () { return null; });
      var mem = core.readSizeMemory()[sizeCategory(p)];
      state.memory = mem ? String(mem) : null;
      var ci = state.kinds.indexOf('colour');
      if (ci > -1 && state.shownColour && p.options[ci].values.indexOf(state.shownColour) > -1 && valueAvailable(ci, state.shownColour)) {
        state.selected[ci] = state.shownColour;
      }
      autoPick();
      els.title.textContent = p.title;
      if (!p.available) setStatus(S.soldOut, true);
      render();
    });
  }

  function close(returnFocus) {
    if (!root || root.hidden) return;
    var opener = state && state.opener;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('vt-qa-lock');
    var finish = function () { root.hidden = true; els.sheet.style.transform = ''; };
    if (animOn()) closeTimer = window.setTimeout(finish, 420); else finish();
    if (returnFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
    state = null;
  }

  function submit() {
    if (!state || !state.product || els.add.getAttribute('aria-busy') === 'true') return;
    var variant = currentVariant();
    if (!variant) {
      var missing = state.selected.indexOf(null);
      var name = missing > -1 ? state.product.options[missing].name : '';
      setStatus(tpl(S.choose, { option: name }), true);
      var first = els.body.querySelector('input[name="vt-qa-o' + missing + '"]:not([disabled])');
      if (first) first.focus();
      return;
    }
    if (!variant.available) { setStatus(CS.errSoldOut || S.soldOut, true); return; }
    els.add.setAttribute('aria-busy', 'true');
    els.add.classList.add('is-loading');
    els.add.textContent = S.adding;
    setStatus('');
    var opener = state.opener;
    var si = state.kinds.indexOf('size');
    var cat = sizeCategory(state.product);
    var sizeVal = si > -1 ? state.selected[si] : null;
    addToCart(variant.id).then(function (data) {
      if (sizeVal && cat) core.writeSizeMemory(cat, sizeVal);
      close(false);
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: variant.id } }));
      core.announce(S.added);
      core.openCartAfterAdd(opener, data && data.sections);
    }).catch(function (err) {
      if (!root.hidden) {
        els.add.removeAttribute('aria-busy');
        els.add.classList.remove('is-loading');
        els.add.textContent = S.add;
        setStatus(err.message || CS.addError, true);
      }
    });
  }

  /* ------------------------------------------------ one variant: add at once */
  var toast = null;
  var toastTimer = null;
  function showToast(text, buttonText, isError, opener) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'vt-qa-toast';
      toast.setAttribute('role', 'status');
      toast.hidden = true;
      toast.innerHTML = '<span class="vt-qa-toast__text"></span><button type="button" class="vt-qa-toast__link"></button>';
      document.body.appendChild(toast);
      toast.querySelector('button').addEventListener('click', function () {
        toast.hidden = true;
        if (!toast.vtError) document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: toast.vtOpener } }));
      });
    }
    toast.vtOpener = opener || null;
    toast.vtError = !!isError;
    toast.classList.toggle('is-error', !!isError);
    toast.hidden = false;
    toast.querySelector('.vt-qa-toast__text').textContent = text || '';
    toast.querySelector('button').textContent = buttonText || '';
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { toast.hidden = true; }, isError ? 7000 : 5000);
  }
  function quickAddOne(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');
    var variantId = parseInt(btn.dataset.vtQaVariant, 10);
    addToCart(variantId).then(function (data) {
      updateSections(data && data.sections);
      // /cart/add.js with "items" answers { items: [...], sections }, so the id comes from the button
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: variantId } }));
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      window.setTimeout(function () { btn.classList.remove('is-added'); }, 2200);
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      showToast(S.added, S.viewBag, false, btn);
    }).catch(function (err) {
      btn.classList.remove('is-loading');
      showToast(err.message || CS.addError, S.close, true, null);
    }).then(function () { btn.removeAttribute('aria-busy'); });
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('[data-vt-qa]');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();
    if (btn.dataset.vtQaVariant) quickAddOne(btn); else open(btn);
  });
  window.addEventListener('pagehide', function () { close(false); });
})();
